import { useMutation, UseMutationResult, useQuery, useQueryClient, UseQueryResult } from "@tanstack/react-query"
import { carrotClient } from "#/utils/CarrotClient"
import { RoutingInfo } from "#/types/routingInfo"
import ResponseError from "../../../types/responseError"
import { HttpStatusCodes } from "#/utils/HttpStatusCodes"
import { HttpErrors } from "#/utils/HttpErrors"
import buildCarrotApiRetryPolicy from "../../../utils/CarrotApiRetryPolicy"
import { carrotPlanEmployeeSettingsQueryKey } from "../hooks/useCarrotPlanEmployeeSettings"
import { CARROT_PLAN_REQUEST_STATUS_KEY } from "#/components/carrot-plans/hooks/useCarrotPlanRequestStatus"
import { ROUTING_STATUS_QUERY_KEY } from "#/components/carrot-plans/hooks/useGetInProgressCarrotPlanRequestType"
export const ROUTING_INFO_QUERY_KEY = "routingInfoQueryKey"

function handleQueryError(error: ResponseError): void {
  const statusCode = error?.response?.status
  if (statusCode && statusCode === HttpStatusCodes.FORBIDDEN) {
    return
  }
}

/**
 * Fetches most recently routing info for the current user which may be INCOMPLETE if the user dropped out of routing early.
 * */
export function useGetRoutingInfo(): UseQueryResult<RoutingInfo, ResponseError> {
  return useQuery({
    queryKey: [ROUTING_INFO_QUERY_KEY],
    queryFn: async () => {
      try {
        return await carrotClient.getCarrotPlansRoutingInfo()
      } catch (error) {
        if (error instanceof ResponseError && error.name === HttpErrors.NOT_FOUND) {
          return null
        }
        throw error
      }
    },
    refetchOnWindowFocus: false,
    refetchOnMount: true,
    refetchOnReconnect: false,
    retry: false,
    onError: handleQueryError
  })
}

/**Warning: Before creating and utilizing a hook like this:
 * Check to see if you can instead set the staleTime as an option on the query.
 */
export function useCheckRoutingInfoQueryCache(): RoutingInfo {
  const queryClient = useQueryClient()
  return queryClient.getQueryData([ROUTING_INFO_QUERY_KEY])
}

export function useStartInitialIntakeRoutingInfo(): UseMutationResult {
  const queryClient = useQueryClient()
  return useMutation(() => carrotClient.startRoutingInfoFlow(), {
    onSuccess: async () => {
      await queryClient.refetchQueries([ROUTING_STATUS_QUERY_KEY])
    },
    retry: false
  })
}

export function useStartJourneyChangeRoutingInfo(): UseMutationResult {
  const queryClient = useQueryClient()
  return useMutation(() => carrotClient.startJourneyChangeRoutingInfoFlow(), {
    onSuccess: () => queryClient.refetchQueries([ROUTING_INFO_QUERY_KEY]),
    retry: false
  })
}

export function useStartCheckInExperienceRoutingInfo(): UseMutationResult {
  const queryClient = useQueryClient()
  return useMutation(() => carrotClient.startCheckInExperienceRoutingInfoFlow(), {
    onSuccess: () => queryClient.refetchQueries([ROUTING_INFO_QUERY_KEY]),
    retry: false
  })
}

export function useStartRenewalRoutingInfo(): UseMutationResult {
  const queryClient = useQueryClient()
  return useMutation(() => carrotClient.startRenewalRoutingInfoFlow(), {
    onSuccess: () => queryClient.refetchQueries([ROUTING_INFO_QUERY_KEY]),
    retry: false
  })
}

export function useRequestInitialCarrotPlan(): UseMutationResult {
  const queryClient = useQueryClient()
  return useMutation(() => carrotClient.requestInitialCarrotPlan(), {
    retry: buildCarrotApiRetryPolicy(3),
    onSuccess: async () => {
      await queryClient.refetchQueries([CARROT_PLAN_REQUEST_STATUS_KEY])
      await queryClient.refetchQueries([ROUTING_INFO_QUERY_KEY])
      await queryClient.refetchQueries([carrotPlanEmployeeSettingsQueryKey])
    }
  })
}

export function useRequestJourneyChangeCarrotPlan(): UseMutationResult {
  const queryClient = useQueryClient()
  return useMutation(() => carrotClient.requestJourneyChangeCarrotPlan(), {
    retry: buildCarrotApiRetryPolicy(3),
    onSuccess: async () => {
      await queryClient.refetchQueries([CARROT_PLAN_REQUEST_STATUS_KEY])
      await queryClient.refetchQueries([ROUTING_INFO_QUERY_KEY])
      await queryClient.refetchQueries([carrotPlanEmployeeSettingsQueryKey])
    }
  })
}

export function useRequestCheckInCarrotPlan(): UseMutationResult {
  const queryClient = useQueryClient()
  return useMutation(() => carrotClient.requestCheckInCarrotPlan(), {
    retry: buildCarrotApiRetryPolicy(3),
    onSuccess: async () => {
      await queryClient.refetchQueries([CARROT_PLAN_REQUEST_STATUS_KEY])
      await queryClient.refetchQueries([ROUTING_INFO_QUERY_KEY])
      await queryClient.refetchQueries([carrotPlanEmployeeSettingsQueryKey])
    }
  })
}

export function useUpdateRoutingInfo(): UseMutationResult {
  const queryClient = useQueryClient()
  const data = queryClient.getQueryData([ROUTING_INFO_QUERY_KEY])
  const startInitialIntakeRoutingInfo = useStartInitialIntakeRoutingInfo()
  return useMutation(
    async (payload) => {
      if (!data) {
        await startInitialIntakeRoutingInfo.mutateAsync(null)
      }
      await carrotClient.updateRoutingInfo(payload)
    },
    {
      onSuccess: async () => {
        await queryClient.refetchQueries([ROUTING_INFO_QUERY_KEY])
      },
      retry: false
    }
  )
}

export function hasQuestionBeenSkipped(questionKey: string, data: RoutingInfo): boolean {
  return (data?.skipFields || []).includes(questionKey)
}
